import {ApplicationRef, Injectable} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {DialogViewModelCreatorService} from '../../../../service/dialog-view-model-creator.service';
import {CommunicationService} from '../../../../service/communication.service';
import {TranslateService} from '@ngx-translate/core';
import {BotatoSpeechRecognition, BotatoSpeechRecognitionEvent} from './speech-recognition-types';
import {
    DialogConfigTextInputValidation
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/DialogConfig/DialogConfig.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let webkitSpeechRecognition: any;

@Injectable({
    providedIn: 'root'
})
export class VoiceRecognitionService {

    public recordedVoice = new BehaviorSubject<boolean>(false);
    public recordedVoice$ = this.recordedVoice.asObservable();
    // Emits whenever finalTranscript or tempWords changed from a speech recognition callback. Those callbacks run
    // outside any Angular event, so the OnPush text input listens to this to re-evaluate its voice validation.
    private transcriptChanged = new Subject<void>();
    public transcriptChanged$ = this.transcriptChanged.asObservable();
    // Created by init(), which every caller runs before start()
    public recognition!: BotatoSpeechRecognition;
    public tempWords = '';
    public recognizing = false;
    public finalTranscript = '';
    public recognitionStopped = false;

    private validation: DialogConfigTextInputValidation | undefined;

    constructor(private applicationRef: ApplicationRef,
                public dialogViewModelCreator: DialogViewModelCreatorService,
                private communicationService: CommunicationService,
                private translationService: TranslateService) {
    }

    public init(android: boolean): void {

        this.recognition = new webkitSpeechRecognition() as BotatoSpeechRecognition;
        this.recognition.interimResults = true;
        this.recognition.lang = this.translationService.getCurrentLang();
        this.recognition.continuous = !android;

        this.recognition.addEventListener('start', () => {
            this.recognizing = true;
            console.log('Speech Recognition started');
        });

        this.recognition.addEventListener('end', async () => {
            console.log('Speech Recognition end');
            this.recognizing = false;
            if (this.recognitionStopped) {
                if (this.finalTranscript.length > 1) {
                    if (this.validation !== undefined) {

                        if (new RegExp(this.validation.regex).test(this.finalTranscript)) {
                            await this.sendMessage();
                        } else {
                            this.tempWords = this.finalTranscript;
                            this.finalTranscript = '';
                            this.transcriptChanged.next();
                        }
                    } else {
                        await this.sendMessage();
                    }
                }
            } else {
                this.recognition.start();
            }
        });

        this.recognition.addEventListener('result', (event: BotatoSpeechRecognitionEvent) => {
            this.tempWords = this.finalTranscript;
            if (typeof (event.results) === 'undefined') {
                // eslint-disable-next-line no-null/no-null
                this.recognition.onend = null;
                this.recognition.stop();
                return;
            }
            for (let i = event.resultIndex; i < event.results.length; ++i) {
                if (event.results[i].isFinal) {
                    this.finalTranscript += event.results[i][0].transcript;
                } else {
                    this.tempWords += event.results[i][0].transcript;
                }
                this.transcriptChanged.next();
                this.applicationRef.tick();
            }
        });

        this.recognition.addEventListener('error', (error) => {
            this.recognition.stop();
            this.tempWords = '';
            console.log('Speech Recognition error', error);
        });
    }

    public start(inputTextValidation: DialogConfigTextInputValidation | undefined): void {
        if (inputTextValidation !== undefined) {

            this.validation = inputTextValidation;
        }
        if (this.recognizing) {
            this.recognition.stop();
            return;
        }
        this.recognitionStopped = false;
        this.recognition.start();
    }

    public stop(): void {
        this.recognitionStopped = true;
        this.recognition.stop();
        console.log('Speech Recognition stopped');
    }

    private async sendMessage(): Promise<void> {
        this.dialogViewModelCreator.addUserTextMessage(this.finalTranscript);
        await this.communicationService.sendMessage(this.finalTranscript, 'text');
        this.finalTranscript = '';
        this.transcriptChanged.next();
        this.applicationRef.tick();
    }
}
